import { getStorageKey } from "#src/hooks/useStorageKey";
import { resolveUITableConfigurationsByKey } from "#src/contexts/UserInterfaceConfigurationResolution.helpers";
import { ExceptionUtils } from "#src/utils/exception";
import type {
  ConfiguredDataTable,
  ConfiguredDataTableHeader,
} from "@validereinc/common-components";
import {
  getUserInterfaceConfigurationSchema,
  type UserInterfaceConfigurationType,
} from "@validereinc/domain";
import { z } from "zod";

export const ConfiguredDataTableHeaderSchema: z.ZodType<ConfiguredDataTableHeader> =
  z.object({
    key: z.string(),
    isFixed: z.boolean().optional(),
    isSelected: z.boolean().optional(),
    headers: z.lazy(() => ConfiguredDataTableHeaderSchema.array()).optional(),
  });

export const TableUserInterfaceConfigurationSchema = z.object({
  columns: z.array(ConfiguredDataTableHeaderSchema),
});
export type TableUserInterfaceConfigurationType = z.infer<
  typeof TableUserInterfaceConfigurationSchema
>;

export const TableUserInterfaceConfigurationFullSchema =
  getUserInterfaceConfigurationSchema(TableUserInterfaceConfigurationSchema);
export type TableUserInterfaceConfigurationFullType = z.infer<
  typeof TableUserInterfaceConfigurationFullSchema
>;

export const extractTableColumnConfigurationFromUITableConfiguration = (
  config: UserInterfaceConfigurationType["configuration"]
): ConfiguredDataTableHeader[] | null => {
  const validationResult =
    TableUserInterfaceConfigurationSchema.safeParse(config);

  if (!validationResult.success) return null;

  return validationResult.data.columns;
};

const readConfiguredTableFromStorage = (
  storageKey: string
): ConfiguredDataTable | null => {
  const rawValue = window.localStorage.getItem(storageKey);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as ConfiguredDataTable;
  } catch {
    return null;
  }
};

export const loadDefaultTableConfigurations =
  (
    userId: string,
    companyId: string,
    tableConfigurations?: UserInterfaceConfigurationType[]
  ) =>
  () => {
    if (!tableConfigurations) {
      return Promise.resolve(0);
    }

    let appliedCount = 0;

    try {
      const resolvedConfigurations = resolveUITableConfigurationsByKey(
        tableConfigurations,
        userId
      );

      resolvedConfigurations.forEach((configuration, serializedKey) => {
        const key = JSON.parse(serializedKey) as string[];
        const cols = extractTableColumnConfigurationFromUITableConfiguration(
          configuration.configuration
        );

        if (!cols) {
          return;
        }

        const storageKey = getStorageKey(
          key,
          `table-configuration`,
          userId,
          companyId
        );
        const existingConfiguration =
          readConfiguredTableFromStorage(storageKey);

        if (existingConfiguration?.source === "user") {
          return;
        }

        appliedCount++;
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            headers: cols,
            source: "user-default",
          } satisfies ConfiguredDataTable)
        );
      });

      return Promise.resolve(appliedCount);
    } catch (err) {
      ExceptionUtils.reportException(err, "warning", {
        sourceComponent: "DefaultUserInterfaceConfigurationProvider",
        hint: "Failed to load default table configurations",
      });
      throw err;
    }
  };

export const clearDefaultTableConfigurations =
  (
    userId: string,
    companyId: string,
    tableConfigurations?: UserInterfaceConfigurationType[]
  ) =>
  () => {
    if (!tableConfigurations) {
      return Promise.resolve(0);
    }

    let clearedCount = 0;

    try {
      const resolvedConfigurations = resolveUITableConfigurationsByKey(
        tableConfigurations,
        userId
      );

      resolvedConfigurations.forEach((configuration, serializedKey) => {
        const key = JSON.parse(serializedKey) as string[];
        const storageKey = getStorageKey(
          key,
          `${configuration.type}-configuration`,
          userId,
          companyId
        );
        const existingConfiguration =
          readConfiguredTableFromStorage(storageKey);

        if (
          existingConfiguration?.source === "user" ||
          existingConfiguration?.source !== "user-default"
        ) {
          return;
        }

        clearedCount++;
        window.localStorage.removeItem(storageKey);
      });

      return Promise.resolve(clearedCount);
    } catch (err) {
      ExceptionUtils.reportException(err, "warning", {
        sourceComponent: "DefaultUserInterfaceConfigurationProvider",
        hint: "Failed to clear default table configurations",
      });
      throw err;
    }
  };
